"use client";

import React, { useState, useEffect, useMemo } from "react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Activity, ArrowRight, Zap } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
const generateInitialData = () => {
  const data = [];
  const now = new Date();
  for (let i = 0; i < 20; i++) {
    const time = new Date(now.getTime() - (20 - i) * 1000);
    data.push({
      time: time.toLocaleTimeString([], {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      }),
      score: 75 + Math.random() * 20
    });
  }
  return data;
};
const chartConfig = {
  score: {
    label: "Lighthouse Score",
    color: "hsl(var(--primary))"
  }
} satisfies ChartConfig;
export default function TheOptimizationPulse() {
  const [data, setData] = useState(generateInitialData());
  const [average, setAverage] = useState(84);
  useEffect(() => {
    const interval = setInterval(() => {
      setData(prevData => {
        const now = new Date();
        const newScore = 70 + Math.random() * 28;
        const newItem = {
          time: now.toLocaleTimeString([], {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit"
          }),
          score: newScore
        };
        return [...prevData.slice(1), newItem];
      });
    }, 2000);
    return () => clearInterval(interval);
  }, []);
  useEffect(() => {
    const avg = data.reduce((acc, curr) => acc + curr.score, 0) / data.length;
    setAverage(Math.round(avg));
  }, [data]);
  return <section id="the-optimization-pulse" className="dark bg-background text-foreground relative overflow-hidden py-20 lg:py-32">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:20px_20px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)]" />
      </div>

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-5 gap-12 lg:gap-16 items-center">
          {}
          <div className="lg:col-span-3 flex flex-col justify-center text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-sm font-medium w-fit mb-6">
              <Zap className="w-4 h-4" />
              <span>Web Performance News</span>
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.1] mb-6 font-sans">
              Web Performance is{" "}
              <span className="text-primary">Revenue</span>
            </h1>
            
            <p className="text-xl text-muted-foreground max-w-2xl mb-8 leading-relaxed">
              Slow sites lose customers. We provide the insights, tools, and techniques you need to build faster, more efficient digital experiences that convert.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link to="/LatestInsightsPage">
                <Button size="lg" className="h-12 px-8 text-base font-semibold shadow-lg shadow-primary/20">
                  Start Optimizing
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/GuidesTutorialsPage">
                <Button variant="outline" size="lg" className="h-12 px-8 text-base bg-transparent border-border hover:bg-accent/10">
                  View Benchmarks
                </Button>
              </Link>
            </div>

            <div className="mt-10 flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-background bg-muted flex items-center justify-center overflow-hidden">
                     <img src={`/img/avatar_user_${i}.png`} alt="User" className="w-full h-full object-cover" />
                  </div>)}
              </div>
              <p>Trusted by 10,000+ developers</p>
            </div>
          </div>

          {}
          <div className="lg:col-span-2 w-full">
            <div className="relative rounded-xl border border-border bg-card/40 backdrop-blur-md shadow-2xl overflow-hidden">
              {}
              <div className="flex items-center justify-between px-6 py-4 border-b border-border/50 bg-card/60">
                <div className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-primary" />
                  <span className="font-semibold text-sm tracking-wide">Lighthouse Pulse</span>
                </div>
                <div className="flex items-center gap-2 bg-primary/10 px-3 py-1 rounded-full border border-primary/20">
                  <span className="relative flex h-2.5 w-2.5">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary"></span>
                  </span>
                  <span className="text-xs font-mono font-medium text-primary">
                    LIVE: Global Avg {average}/100
                  </span>
                </div>
              </div>

              {}
              <div className="p-6">
                <ChartContainer config={chartConfig} className="h-[250px] w-full">
                  <AreaChart data={data} margin={{
                  top: 10,
                  right: 0,
                  left: -20,
                  bottom: 0
                }}>
                    <defs>
                      <linearGradient id="fillScore" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="var(--color-primary)" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="var(--color-primary)" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.2} />
                    <XAxis dataKey="time" tickLine={false} axisLine={false} tickMargin={8} minTickGap={30} tickFormatter={value => value.slice(3)} stroke="var(--color-muted-foreground)" fontSize={10} />
                    <YAxis domain={[50, 100]} tickLine={false} axisLine={false} tickMargin={8} stroke="var(--color-muted-foreground)" fontSize={10} />
                    <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dot" hideLabel />} />
                    <Area dataKey="score" type="monotone" fill="url(#fillScore)" fillOpacity={0.4} stroke="var(--color-primary)" strokeWidth={2} isAnimationActive={true} animationDuration={1000} />
                  </AreaChart>
                </ChartContainer>
              </div>

              {}
              <div className="grid grid-cols-3 divide-x divide-border/50 border-t border-border/50 bg-card/30">
                {[{
                label: "Sites Analyzed",
                value: "500+"
              }, {
                label: "Avg TBT",
                value: "120ms"
              }, {
                label: "Avg LCP",
                value: "1.2s"
              }].map((stat, i) => <div key={i} className="px-4 py-3 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-0.5">
                      {stat.label}
                    </div>
                    <div className="text-sm font-bold font-mono text-foreground">
                      {stat.value}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}